import React from 'react';
import { Grid, TextField, MenuItem, Autocomplete, Typography, Divider, Box, FormControlLabel, RadioGroup, Radio } from '@mui/material';
import { antibioticOptions, paracetamolOptions, injUketOptions, concentrationOptions, painScoreOptions, medicationOptions, insulinOrderOptions } from '../constants/options';

const MedicationInstructionsSection = ({ formData, setFormData, handleInputChange }) => {
  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#008080', mb: 2, fontWeight: 600 }}>
        Medications & Analgesia
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={antibioticOptions}
            value={formData.antibiotic_advised || ""}
            onInputChange={(e, newVal) => setFormData(prev => ({ ...prev, antibiotic_advised: newVal }))}
            renderInput={(params) => (
              <TextField {...params} label="Antibiotic Advised" name="antibiotic_advised" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Inj. Pan 40mg IV</Typography>
          <RadioGroup 
            row 
            name="inj_pan_40mg_iv" 
            value={formData.inj_pan_40mg_iv || ''} 
            onChange={handleInputChange}
          >
            <FormControlLabel value="OD" control={<Radio />} label="OD" />
            <FormControlLabel value="BD" control={<Radio />} label="BD" />
          </RadioGroup>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={paracetamolOptions}
            value={formData.inj_paracetamol_1gm_iv || ""}
            onInputChange={(e, newVal) => setFormData(prev => ({ ...prev, inj_paracetamol_1gm_iv: newVal }))}
            renderInput={(params) => (
              <TextField {...params} label="Inj. Paracetamol 1GM IV" name="inj_paracetamol_1gm_iv" />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="Time"
            name="inj_paracetamol_time"
            type="time"
            value={formData.inj_paracetamol_time || ''}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="Days"
            name="inj_paracetamol_days"
            type="number"
            value={formData.inj_paracetamol_days || ''}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={injUketOptions}
            value={formData.inj_ukat || ""}
            onInputChange={(e, newVal) => setFormData(prev => ({ ...prev, inj_ukat: newVal }))}
            renderInput={(params) => (
              <TextField {...params} label="Inj. UKAT" name="inj_ukat" />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="Time"
            name="inj_ukat_time"
            type="time"
            value={formData.inj_ukat_time || ''}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="Days"
            name="inj_ukat_days"
            type="number"
            value={formData.inj_ukat_days || ''}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Epidural Infusion</Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={concentrationOptions}
            value={formData.concentration || ""}
            onInputChange={(e, newVal) => setFormData(prev => ({ ...prev, concentration: newVal }))}
            renderInput={(params) => (
              <TextField {...params} label="Concentration" name="concentration" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Date/Time Started"
            name="concentration_date_time"
            type="datetime-local"
            value={formData.concentration_date_time || ''}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="For Breakthrough Pain Analgesia Prescribed"
            name="pain_analgesia_prescribed"
            value={formData.pain_analgesia_prescribed || ""}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={painScoreOptions}
            value={formData.pain_score || ""}
            onChange={(e, newVal) => setFormData(prev => ({ ...prev, pain_score: newVal }))}
            renderInput={(params) => (
              <TextField {...params} label="VAS Pain Score > 4-5/10" name="pain_score" />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="T. Alprax H.S"
                name="t_alprax_hs"
                value={formData.t_alprax_hs || ""}
                onChange={handleInputChange}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </Grid>
            {formData.t_alprax_hs === "Yes" && (
              <Grid item xs={12}>
                <RadioGroup 
                  row 
                  name="t_alprax_dose" 
                  value={formData.t_alprax_dose || ""} 
                  onChange={handleInputChange}
                >
                  <FormControlLabel value="0.25 mg" control={<Radio />} label="0.25 mg" />
                  <FormControlLabel value="0.5 mg" control={<Radio />} label="0.5 mg" />
                </RadioGroup>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', mt: 2 }}>
            Other Routine Medication If Any to be Continued
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            name="other_medications"
            value={formData.other_medications || ""}
            onChange={handleInputChange}
            placeholder="Enter other routine medications..."
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', mt: 2 }}>
            CBG and Insulin Orders
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            name="cbg_insulin_orders"
            value={formData.cbg_insulin_orders || ""}
            onChange={handleInputChange}
            placeholder="Enter CBG and insulin orders..."
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default MedicationInstructionsSection;
